import { useEffect, useRef, useState } from 'react'
import to from 'await-to-js'
import { toast } from 'react-toastify'
import { Modal } from '../../../components/UI/Modal/Modal'
import { Select } from '../../../components/UI/Select/Select'
import CustomCheckbox from 'components/Checkbox/checkbox'
import useApiService from 'services/api'
import { toCalendarSelectItems } from 'services/calendarsApi'
import {
  type Grant,
  type UpdateGrantPreferencesMutationRequest,
  useUpdateGrantPreferences,
} from 'services/grants'
import { GoogleButton, OutlookButton } from './ProviderButtons'
import { useCalendars } from './useCalendars'

type ModalView = 'edit' | 'disconnect'

interface ManageConnectionModalProps {
  open: boolean
  grant: Grant | null
  onClose: () => void
  onRefetch: () => void
}

export function ManageConnectionModal({
  open,
  grant,
  onClose,
  onRefetch,
}: ManageConnectionModalProps) {
  const api = useApiService()
  const updateGrantPreferencesMutation = useUpdateGrantPreferences()

  const [emailChecked, setEmailChecked] = useState(grant?.has_email ?? false)
  const [calendarChecked, setCalendarChecked] = useState(
    grant?.has_calendar ?? false
  )
  const [isEmailShared, setIsEmailShared] = useState(
    grant?.is_email_shared ?? false
  )
  const [isCalendarShared, setIsCalendarShared] = useState(
    grant?.is_calendar_shared ?? false
  )
  const [saving, setSaving] = useState(false)
  const [view, setView] = useState<ModalView>('edit')
  const [featureToRemove, setFeatureToRemove] = useState<'email' | 'calendar'>(
    'email'
  )

  const hasCalendar = grant?.has_calendar ?? false
  const integrationId = grant?.calendar_integration_id ?? null
  const {
    calendars,
    selectedId: selectedCalendarId,
    setSelectedId: setSelectedCalendarId,
    loading: calendarsLoading,
  } = useCalendars(integrationId, hasCalendar)

  const originalCalendarIdRef = useRef<number | null>(null)

  // Capture the original selected calendar when it loads
  useEffect(() => {
    if (selectedCalendarId !== null && originalCalendarIdRef.current === null) {
      originalCalendarIdRef.current = selectedCalendarId
    }
  }, [selectedCalendarId])

  if (!grant) return null

  const originalCalendarId = originalCalendarIdRef.current
  const emailAdded = emailChecked && !grant.has_email
  const calendarAdded = calendarChecked && !grant.has_calendar
  const emailRemoved = !emailChecked && grant.has_email
  const calendarRemoved = !calendarChecked && grant.has_calendar
  const isEmailSharedChanged =
    grant.has_email && isEmailShared !== grant.is_email_shared
  const isCalendarSharedChanged =
    grant.has_calendar && isCalendarShared !== grant.is_calendar_shared
  const calendarChanged = selectedCalendarId !== originalCalendarId
  const needsAddFeature = emailAdded || calendarAdded
  const needsRemoveFeature = emailRemoved || calendarRemoved
  const bothUnchecked = !emailChecked && !calendarChecked

  const hasChanges =
    needsAddFeature ||
    needsRemoveFeature ||
    isEmailSharedChanged ||
    isCalendarSharedChanged ||
    calendarChanged

  const calendarSelectItems = toCalendarSelectItems(calendars)

  const handleAddFeature = async (provider: string) => {
    const features: string[] = []
    if (emailChecked) features.push('email')
    if (calendarChecked) features.push('calendar')

    const [err, response] = await to(
      api.emailIntegration.getEmailIntegrationAuthUrl({
        provider,
        isShared: isEmailShared,
        isCalendarShared,
        email: grant.email,
        features,
      })
    )
    if (err) {
      toast.error('Error connecting account')
      return
    }
    if (response?.auth_url) {
      window.location.href = response.auth_url
    }
  }

  const buildPayload = (
    includeRemove?: 'email' | 'calendar'
  ): UpdateGrantPreferencesMutationRequest => {
    const payload: UpdateGrantPreferencesMutationRequest = {}
    if (isEmailSharedChanged) payload.is_email_shared = isEmailShared
    if (isCalendarSharedChanged) payload.is_calendar_shared = isCalendarShared
    if (calendarChanged && selectedCalendarId) {
      payload.selected_calendar_id = selectedCalendarId
    }
    if (includeRemove) payload.remove_feature = includeRemove
    return payload
  }

  const handleSave = async () => {
    if (bothUnchecked || !hasChanges) return

    // If removing a feature, transition to disconnect confirmation view
    if (needsRemoveFeature) {
      setFeatureToRemove(emailRemoved ? 'email' : 'calendar')
      setView('disconnect')
      return
    }

    setSaving(true)
    try {
      const payload = buildPayload()
      const [err] = await to(
        updateGrantPreferencesMutation.mutateAsync({
          grant_id: grant.grant_id,
          data: payload,
        })
      )
      if (err) {
        toast.error('Error updating preferences')
        return
      }
      toast.success('Preferences updated')
      onRefetch()
      onClose()
    } finally {
      setSaving(false)
    }
  }

  const handleDisconnect = async () => {
    setSaving(true)
    try {
      const payload = buildPayload(featureToRemove)
      const [err] = await to(
        updateGrantPreferencesMutation.mutateAsync({
          grant_id: grant.grant_id,
          data: payload,
        })
      )
      if (err) {
        toast.error('Error updating preferences')
        return
      }

      const name = featureToRemove === 'email' ? 'Email' : 'Calendar'
      toast.success(`${name} feature removed`)
      onRefetch()
      onClose()
    } finally {
      setSaving(false)
    }
  }

  const handleBackToEdit = () => {
    setView('edit')
    // Reset checkboxes to grant's original state
    setEmailChecked(grant.has_email)
    setCalendarChecked(grant.has_calendar)
    setIsEmailShared(grant.is_email_shared)
    setIsCalendarShared(grant.is_calendar_shared)
    setSelectedCalendarId(originalCalendarId)
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      if (view === 'disconnect') {
        handleBackToEdit()
      } else {
        onClose()
      }
    }
  }

  // ─── Disconnect confirmation view ───
  if (view === 'disconnect') {
    const isEmail = featureToRemove === 'email'
    const description = isEmail
      ? 'Disconnecting email will permanently delete all synced email data for this account. This action cannot be undone.'
      : 'Disconnecting your calendar will permanently delete all calendar data and synced events for this account. This action cannot be undone.'

    return (
      <Modal
        open={open}
        onOpenChange={handleOpenChange}
        title={
          <div className="flex flex-col gap-1">
            <span className="text-xs font-light tracking-[0.03em] text-fg">
              INTEGRATIONS
            </span>
            <span className="text-xl font-semibold text-fg leading-7">
              Edit Account Settings
            </span>
          </div>
        }
        onAccept={handleDisconnect}
        acceptButtonText="Disconnect"
        blockAccept={saving}
        hideCancelButton
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm font-normal text-gray-500 leading-5">
            {description}
          </p>

          {/* Account email */}
          <div className="flex items-center px-3 py-2 gap-2 w-full h-10 bg-surface-alt rounded-lg">
            <span className="text-base font-normal text-fg leading-6">
              {grant.email}
            </span>
          </div>
        </div>
      </Modal>
    )
  }

  // ─── Edit view ───
  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title={
        <div className="flex flex-col gap-1">
          <span className="text-xs font-light tracking-[0.03em] text-fg">
            INTEGRATIONS
          </span>
          <span className="text-xl font-semibold text-fg leading-7">
            Edit Account Preferences
          </span>
        </div>
      }
      onAccept={needsAddFeature ? undefined : handleSave}
      acceptButtonText="Update"
      blockAccept={bothUnchecked || !hasChanges || saving}
      hideCancelButton={needsAddFeature}
    >
      <div className="flex flex-col gap-6">
        {/* Account section */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-fg leading-5">Account</h3>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center px-3 py-2 gap-2 w-full h-10 bg-surface-alt rounded-lg">
              <span className="text-base font-normal text-fg leading-6">
                {grant.email}
              </span>
            </div>
          </div>
        </div>

        {/* Email Preferences section */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-[6px]">
            <h3 className="text-sm font-medium text-fg leading-5">
              Email Preferences
            </h3>
            <div className="flex items-center gap-2">
              <CustomCheckbox
                id="manage_email"
                checked={emailChecked}
                onChange={(e) => setEmailChecked(e.target.checked)}
                value={emailChecked ? 'true' : 'false'}
              />
              <label
                htmlFor="manage_email"
                className="text-xs font-medium text-fg leading-5"
              >
                Sync email to First Bite
              </label>
            </div>
            {emailChecked && (
              <div className="flex items-center gap-2">
                <CustomCheckbox
                  id="manage_is_email_shared"
                  checked={isEmailShared}
                  onChange={(e) => setIsEmailShared(e.target.checked)}
                  value={isEmailShared ? 'true' : 'false'}
                />
                <label
                  htmlFor="manage_is_email_shared"
                  className="text-xs font-medium text-fg leading-5"
                >
                  Share email visibility with my team
                </label>
              </div>
            )}
          </div>

          {/* Calendar Preferences section */}
          <div className="flex flex-col gap-[6px]">
            <h3 className="text-sm font-medium text-fg leading-5">
              Calendar Preferences
            </h3>
            <p className="text-xxs text-gray-500 leading-[140%]">
              This is a one-way sync: First Bite-created events will sync to
              your calendar, but Outlook- or Google-created events will NOT
              appear in First Bite.
            </p>
            {calendarChecked &&
              grant.has_calendar &&
              calendarSelectItems.length > 0 && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-fg leading-5">
                    Selected Calendar
                  </label>
                  <Select<number>
                    items={calendarSelectItems}
                    selected={selectedCalendarId ?? undefined}
                    onSelect={(val) => setSelectedCalendarId(val)}
                    nullSelectionLabel="Select a calendar..."
                    matchTriggerWidth
                    buttonVariant="secondary"
                    disabled={calendarsLoading}
                  />
                </div>
              )}
            <div className="flex flex-col gap-[6px]">
              <div className="flex items-center gap-2">
                <CustomCheckbox
                  id="manage_calendar"
                  checked={calendarChecked}
                  onChange={(e) => setCalendarChecked(e.target.checked)}
                  value={calendarChecked ? 'true' : 'false'}
                />
                <label
                  htmlFor="manage_calendar"
                  className="text-xs font-medium text-fg leading-5"
                >
                  Integrate calendar with First Bite
                </label>
              </div>
              {calendarChecked && (
                <div className="flex items-center gap-2">
                  <CustomCheckbox
                    id="manage_is_calendar_shared"
                    checked={isCalendarShared}
                    onChange={(e) => setIsCalendarShared(e.target.checked)}
                    value={isCalendarShared ? 'true' : 'false'}
                  />
                  <label
                    htmlFor="manage_is_calendar_shared"
                    className="text-xs font-medium text-fg leading-5"
                  >
                    Share calendar with my team
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        {bothUnchecked && (
          <p className="text-xs text-red-500">
            Use &quot;Delete connection&quot; to remove all features.
          </p>
        )}

        {/* Provider button — only shown when adding a feature */}
        {needsAddFeature && (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-gray-500">
              Adding a new feature requires re-authentication.
            </p>
            {grant.provider === 'google' ? (
              <GoogleButton onClick={() => void handleAddFeature('google')} />
            ) : (
              <OutlookButton
                onClick={() => void handleAddFeature('microsoft')}
              />
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}
